/**
 * Throttled inline search across the active scope.
 *
 * Renders the input at the top of the file-explorer sidebar. Hits drop
 * down into a flat list below the input — clicking one opens the file
 * in the main viewer.
 *
 * Per UI guidelines: empty state is a tiny text-only hint, no big
 * illustrations.
 */
import type { FsSearchHit } from '@chaos/types/fs';
import { useQuery } from '@tanstack/react-query';
import { FileIcon, Search as SearchIcon, X } from 'lucide-react';
import * as React from 'react';

import { fsApi, fsKeys } from '../../lib/api/fs';
import { cn } from '../../lib/utils';

interface FileSearchProps {
  scope: string | null;
  onSelectHit: (hit: FsSearchHit) => void;
}

const DEBOUNCE_MS = 300;

export function FileSearch({ scope, onSelectHit }: FileSearchProps) {
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');

  React.useEffect(() => {
    if (!query) {
      setDebounced('');
      return undefined;
    }
    const id = window.setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const hitsQ = useQuery({
    queryKey: fsKeys.search(debounced, scope ?? undefined),
    queryFn: () => fsApi.search({ q: debounced, ...(scope ? { root: scope } : {}), limit: 50 }),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });

  const showResults = debounced.length > 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search files"
          placeholder="Search…"
          className={cn(
            'h-8 w-full rounded border border-border bg-background pl-7 pr-7 text-[12px] text-foreground',
            'placeholder:text-muted-foreground outline-none transition-colors',
            'focus-visible:ring-1 focus-visible:ring-foreground/40',
          )}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="absolute right-1 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>

      {showResults && (
        <div
          className="rounded border border-border bg-background py-1 text-[12px]"
          role="listbox"
          aria-label="Search results"
        >
          {hitsQ.isLoading && <p className="px-2 py-1 text-muted-foreground">Searching…</p>}
          {!hitsQ.isLoading && (hitsQ.data?.hits.length ?? 0) === 0 && (
            <p className="px-2 py-1 text-muted-foreground/70">No matches.</p>
          )}
          {(hitsQ.data?.hits ?? []).map((hit) => (
            <SearchHitRow
              key={`${hit.path}:${hit.line ?? 0}`}
              hit={hit}
              onSelect={() => onSelectHit(hit)}
            />
          ))}
          {hitsQ.data?.truncated && (
            <p className="px-2 py-1 text-[10px] text-muted-foreground/70">
              More results truncated…
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SearchHitRow({ hit, onSelect }: { hit: FsSearchHit; onSelect: () => void }) {
  const filename = hit.path.split('/').pop() ?? hit.path;
  return (
    <button
      type="button"
      role="option"
      aria-selected={false}
      onClick={onSelect}
      className="flex w-full items-start gap-1.5 px-2 py-1 text-left text-[12px] transition-colors hover:bg-foreground/5"
    >
      <FileIcon className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate font-medium text-foreground">{filename}</span>
          {hit.line && (
            <span className="shrink-0 text-[10px] text-muted-foreground">L{hit.line}</span>
          )}
        </div>
        {hit.snippet && (
          <p className="truncate font-mono text-[11px] text-muted-foreground">{hit.snippet}</p>
        )}
        <p className="truncate text-[10px] text-muted-foreground/70">{hit.path}</p>
      </div>
    </button>
  );
}
